import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { SidebarNav } from "./SidebarNav";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/Components/ui/tooltip";

export function Sidebar({ className, onToggle }) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onToggle?.(newState);
  };

  // Notify parent of initial state
  useEffect(() => {
    onToggle?.(isCollapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TooltipProvider>
      <aside
        className={cn(
          "fixed left-0 top-0 z-[60] h-screen",
          "border-r border-white/10 bg-[#20252b]",
          "transition-all duration-300 ease-in-out",
          isCollapsed ? "w-20" : "w-72",
          className
        )}
      >
        {/* Subtle surface treatment */}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0)_28%)]" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-sunridge-cyan/30 to-transparent" />

        {/* Toggle button */}
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              onClick={toggleSidebar}
              className={cn(
                "absolute -right-3 top-20 z-[100]",
                "h-7 w-7 rounded-full",
                "border border-white/10 bg-[#20252b]",
                "flex items-center justify-center",
                "text-slate-400 hover:text-white hover:border-sunridge-cyan/40",
                "hover:bg-[#2b3037]",
                "transition-all duration-200",
                "shadow-lg shadow-black/20"
              )}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight size={14} />
              ) : (
                <ChevronLeft size={14} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-[#171b20] border-white/10 text-white">
            {isCollapsed ? "Expand" : "Collapse"}
          </TooltipContent>
        </Tooltip>

        {/* Navigation content */}
        <div className="relative h-full">
          <SidebarNav isCollapsed={isCollapsed} />
        </div>

        {/* Bottom gradient fade */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#20252b] to-transparent" />
      </aside>
    </TooltipProvider>
  );
}

export default Sidebar;
